import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/toast";
import { BrandHeading } from "@/components/BrandFrame";

interface PatchStage {
  uploadId: string; rowCount: number; patchCount: number;
  skippedCount: number; skippedSample: string[]; priceChanges: number; warnings: any[];
}

export default function AdminUploadPage() {
  const qc = useQueryClient();
  const { push } = useToast();
  const patchInputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<PatchStage | null>(null);
  const [drag, setDrag] = useState(false);

  const uploadsQ = useQuery({ queryKey: ["uploads"], queryFn: api.listUploads });

  const uploadPatch = useMutation({
    mutationFn: (f: File) => api.uploadPricePatch(f),
    onSuccess: (r) => {
      setStage({ uploadId: r.uploadId, rowCount: r.rowCount, patchCount: r.patchCount, skippedCount: r.skippedCount, skippedSample: r.skippedSample, priceChanges: r.priceChanges, warnings: r.warnings });
      qc.invalidateQueries({ queryKey: ["uploads"] });
      push({ title: `ตรวจพบราคาที่เปลี่ยน ${r.priceChanges.toLocaleString()} รายการ` });
    },
    onError: (e: any) => push({ title: "อัปโหลดไม่สำเร็จ", description: e.message, variant: "error" }),
  });

  const applyPatch = useMutation({
    mutationFn: (id: string) => api.applyPricePatch(id),
    onSuccess: (r) => {
      setStage(null);
      qc.invalidateQueries({ queryKey: ["uploads"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["adminProducts"] });
      push({ title: `อัปเดตราคาแล้ว ${r.updates.toLocaleString()} รายการ` });
    },
    onError: (e: any) => push({ title: "ปรับใช้ไม่สำเร็จ", description: e.message, variant: "error" }),
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.rejectUpload(id),
    onSuccess: () => { setStage(null); qc.invalidateQueries({ queryKey: ["uploads"] }); push({ title: "ยกเลิกแล้ว" }); },
  });

  async function downloadTemplate() {
    try {
      const [{ buildPriceTemplateXlsx }, r] = await Promise.all([
        import("@/lib/xlsx-builder"),
        fetch("/api/admin/export/price-template", { credentials: "include" }),
      ]);
      if (!r.ok) throw new Error(await r.text());
      const { rows } = await r.json();
      const buf = await buildPriceTemplateXlsx(rows);
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BBT_PriceUpdate_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      push({ title: "ดาวน์โหลดไม่สำเร็จ", description: e.message, variant: "error" });
    }
  }

  return (
    <div className="space-y-6">
      <BrandHeading>อัปโหลดไฟล์ราคา</BrandHeading>

      <Card className="border-amber-400/50">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>แก้ไขราคา — Price Update</CardTitle>
              <CardDescription className="mt-1">
                ดาวน์โหลดแม่แบบ → แก้ไขราคา → อัปโหลดกลับ ·{" "}
                <strong>เฉพาะราคา</strong> — ไม่เพิ่ม/ลบ SKU · ไม่แตะข้อมูลสินค้า
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="shrink-0 border-amber-400 text-amber-700 hover:bg-amber-50"
            >
              <Download className="h-4 w-4" /> ดาวน์โหลดแม่แบบ
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 space-y-1">
            <div>
              <strong>ขั้นตอน:</strong>{" "}
              1) กดปุ่ม "ดาวน์โหลดแม่แบบ" →{" "}
              2) แก้ไขคอลัมน์ <strong>I (SALE PRICE)</strong> หรือ <strong>L–Q (ราคาตามระดับ)</strong> →{" "}
              3) อัปโหลดไฟล์นั้นกลับมาที่นี่
            </div>
            <div className="text-amber-600">
              คอลัมน์ A–H, J–K, R ถูกล็อกฝั่งเซิร์ฟเวอร์ — ระบบจะเพิกเฉยต่อการเปลี่ยนแปลงในคอลัมน์เหล่านั้น
            </div>
          </div>

          {!stage && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) uploadPatch.mutate(f); }}
              onClick={() => patchInputRef.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 py-12 text-center transition-colors duration-300 ${drag ? "border-amber-500 bg-amber-500/5" : "border-amber-300 hover:border-amber-500 hover:bg-amber-50/50"}`}
            >
              <Upload className="h-8 w-8 text-amber-500" />
              <div className="font-display text-base font-bold text-amber-700">ลากไฟล์ BBT_PriceUpdate_*.xlsx มาวางหรือกดเพื่อเลือก</div>
              <div className="text-xs text-amber-500/80">อัปโหลดเฉพาะไฟล์ที่ดาวน์โหลดจากปุ่มด้านบน</div>
              <input ref={patchInputRef} type="file" accept=".xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPatch.mutate(f); e.target.value = ""; }} />
            </div>
          )}

          {uploadPatch.isPending && (
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-700">กำลังอ่านและตรวจสอบไฟล์…</div>
          )}

          {stage && (
            <Card className="border-amber-400/30">
              <CardHeader>
                <CardTitle>สรุปการเปลี่ยนแปลงราคา</CardTitle>
                <CardDescription>รายการ <code>{stage.uploadId}</code> · {stage.rowCount.toLocaleString()} แถวในไฟล์</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Stat label="ราคาที่เปลี่ยน" value={stage.priceChanges.toLocaleString()} tone="amber" />
                  <Stat label="SKU ที่จะอัปเดต" value={stage.patchCount.toLocaleString()} tone="default" />
                  <Stat label="ข้าม (ไม่มีในระบบ)" value={stage.skippedCount.toLocaleString()} tone="orange" />
                </div>
                {stage.skippedSample.length > 0 && (
                  <Sample title="SKU ที่ข้ามเพราะไม่มีในระบบ" items={stage.skippedSample} />
                )}
                {stage.warnings.length > 0 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-amber-900">
                    <div className="mb-1 text-xs font-semibold uppercase">คำเตือน ({stage.warnings.length})</div>
                    <ul className="list-disc pl-4 text-xs">
                      {stage.warnings.slice(0, 30).map((w: any, i: number) => (
                        <li key={i}>{w.row > 0 ? `แถว ${w.row} · ` : ""}{w.field}: {w.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="primary" onClick={() => applyPatch.mutate(stage.uploadId)} disabled={applyPatch.isPending || stage.priceChanges === 0}>
                    <Check className="h-4 w-4" />
                    {applyPatch.isPending ? "กำลังอัปเดต…" : stage.priceChanges === 0 ? "ไม่มีราคาที่เปลี่ยน" : `ยืนยัน — อัปเดต ${stage.priceChanges.toLocaleString()} ราคา`}
                  </Button>
                  <Button variant="outline" onClick={() => reject.mutate(stage.uploadId)} disabled={reject.isPending}>
                    <X className="h-4 w-4" /> ยกเลิก
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>ประวัติการอัปโหลด</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">ชื่อไฟล์</th>
                <th>โดย</th>
                <th>วันเวลา</th>
                <th>จำนวน</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {(uploadsQ.data?.uploads ?? []).map((u: any) => (
                <tr key={u.id} className="border-t border-border/50">
                  <td className="py-2 font-mono text-xs">{u.filename}</td>
                  <td className="text-xs">{u.uploaded_by_username ?? "—"}</td>
                  <td className="text-xs text-muted-foreground">{new Date(u.uploaded_at).toLocaleString("th-TH")}</td>
                  <td>{u.row_count.toLocaleString()}</td>
                  <td>
                    <Badge variant={u.status === "applied" ? "wholesale" : u.status === "rejected" ? "net" : "license"}>
                      {u.status === "applied" ? "ใช้งานแล้ว" : u.status === "rejected" ? "ยกเลิก" : "รอยืนยัน"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: "default" | "emerald" | "orange" | "amber" }) {
  const toneCls =
    tone === "emerald" ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700"
    : tone === "orange" ? "border-accent-orange/30 bg-accent-orange/5 text-accent-orange"
    : tone === "amber" ? "border-amber-500/30 bg-amber-500/5 text-amber-700"
    : "border-border bg-secondary";
  return (
    <div className={`rounded-md border p-3 ${toneCls}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="mt-1 font-display text-2xl font-black">{value}</div>
    </div>
  );
}

function Sample({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-md border border-border bg-secondary/40 p-3">
      <div className="mb-1 text-xs font-semibold uppercase">{title}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((p) => <code key={p} className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px]">{p}</code>)}
      </div>
    </div>
  );
}
